import { Controller } from "@hotwired/stimulus"

// Unified library-item selector. Generalizes book-selector to handle multiple
// item types (Book, BoxSet, Memorabilia) in a single search box. Each selected
// item is submitted into the field name configured for its type, so the server
// receives the same collection[book_ids][] / collection[box_set_ids][] /
// collection[memorabilia_ids][] params it already expects.
//
// Values:
//   searchUrl   — endpoint returning [{id,type,title,subtitle,image_url}]
//   selected    — initial selected items (same shape as search results)
//   fieldNames  — { Book: "collection[book_ids][]", BoxSet: "...", ... }
export default class extends Controller {
  static targets = ["search", "results", "selected", "hiddenInputs"]
  static values = {
    searchUrl: String,
    selected: Array,
    fieldNames: Object
  }

  // Badge color per display label. Books come in several kinds (Book,
  // Pamphlet, Magazine, Newspaper, Other Media, Memorabilia) — each gets its
  // own color so the search results read truthfully instead of labeling a
  // magazine as "Book".
  BADGE_COLORS = {
    "Book":        "bg-amber-100 text-amber-800 border-amber-200",
    "Pamphlet":    "bg-lime-100 text-lime-800 border-lime-200",
    "Magazine":    "bg-pink-100 text-pink-800 border-pink-200",
    "Newspaper":   "bg-slate-100 text-slate-700 border-slate-200",
    "Other Media": "bg-teal-100 text-teal-800 border-teal-200",
    "Memorabilia": "bg-purple-100 text-purple-800 border-purple-200",
    "Box Set":     "bg-blue-100 text-blue-800 border-blue-200"
  }

  connect() {
    this.searchTimeout = null
    this.renderSelected()
  }

  // Unique key so a Book #5 and a BoxSet #5 never collide. `type` (the
  // polymorphic item_type) is the right key — every Book-kind shares "Book".
  key(item) {
    return `${item.type}:${item.id}`
  }

  badgeFor(item) {
    return this.BADGE_COLORS[item.label] || "bg-beige-100 text-beige-700 border-beige-200"
  }

  // Box-set covers are landscape — contain them; everything else fills.
  fitFor(item) {
    return item.type === "BoxSet" ? "object-contain bg-white" : "object-cover"
  }

  labelFor(item) {
    return item.label || item.type
  }

  search() {
    const query = this.searchTarget.value.trim()
    clearTimeout(this.searchTimeout)
    if (query.length < 2) {
      this.resultsTarget.innerHTML = ""
      this.resultsTarget.classList.add("hidden")
      return
    }
    this.searchTimeout = setTimeout(() => this.performSearch(query), 300)
  }

  async performSearch(query) {
    try {
      const response = await fetch(`${this.searchUrlValue}?q=${encodeURIComponent(query)}`, {
        headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" }
      })
      if (!response.ok) throw new Error("Search failed")
      const data = await response.json()
      // Response shape is { items, truncated }; tolerate a bare array too.
      const items = Array.isArray(data) ? data : (data.items || [])
      const truncated = !Array.isArray(data) && Boolean(data.truncated)
      this.renderResults(items, truncated)
    } catch (error) {
      console.error("Item search error:", error)
      this.resultsTarget.innerHTML = `<div class="p-3 text-sm text-red-600">Search failed. Please try again.</div>`
      this.resultsTarget.classList.remove("hidden")
    }
  }

  renderResults(items, truncated = false) {
    if (!items.length) {
      this.resultsTarget.innerHTML = `<div class="p-3 text-sm text-beige-500 font-fira">Nothing found</div>`
      this.resultsTarget.classList.remove("hidden")
      return
    }

    const selectedKeys = new Set(this.selectedValue.map(i => this.key(i)))

    const itemsHtml = items.map(item => {
      const isSelected = selectedKeys.has(this.key(item))
      return `
        <label class="flex items-center gap-3 p-2 hover:bg-beige-50 transition-colors ${isSelected ? 'opacity-60' : 'cursor-pointer'}">
          <input type="checkbox"
                 class="form-checkbox flex-shrink-0 h-4 w-4"
                 data-item-id="${item.id}"
                 data-item-type="${this.escapeAttr(item.type)}"
                 data-item-label="${this.escapeAttr(item.label || '')}"
                 data-item-title="${this.escapeAttr(item.title)}"
                 data-item-subtitle="${this.escapeAttr(item.subtitle || '')}"
                 data-item-image="${this.escapeAttr(item.image_url || '')}"
                 ${isSelected ? 'disabled checked' : ''}>
          <div class="w-10 h-14 flex-shrink-0 bg-beige-200 rounded overflow-hidden flex items-center justify-center">
            ${item.image_url ? `<img src="${this.escapeAttr(item.image_url)}" alt="" class="w-full h-full ${this.fitFor(item)}">` : ''}
          </div>
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-1.5">
              <span class="text-[10px] font-fira uppercase tracking-wider px-1.5 py-0.5 rounded border ${this.badgeFor(item)}">${this.escapeHtml(this.labelFor(item))}</span>
              <p class="text-sm font-medium text-beige-900 truncate">${this.escapeHtml(item.title)}</p>
            </div>
            ${item.subtitle ? `<p class="text-xs text-beige-500 font-fira truncate mt-0.5">${this.escapeHtml(item.subtitle)}</p>` : ''}
          </div>
          ${isSelected ? '<span class="text-xs text-green-600 font-fira flex-shrink-0">Added</span>' : ''}
        </label>
      `
    }).join("")

    const footerHtml = `
      <div class="sticky bottom-0 bg-white border-t border-beige-200 p-2 flex items-center justify-between gap-2">
        <span class="text-xs text-beige-500 font-fira">${truncated ? "Showing top 50 — keep typing to narrow" : "Check items, then click Add"}</span>
        <button type="button" class="btn-primary text-sm px-3 py-1.5" data-action="item-selector#addSelected">Add Selected</button>
      </div>
    `

    this.resultsTarget.innerHTML = itemsHtml + footerHtml
    this.resultsTarget.classList.remove("hidden")
  }

  addSelected(event) {
    event.preventDefault()
    event.stopPropagation()

    const checkboxes = this.resultsTarget.querySelectorAll('input[type="checkbox"]:checked:not(:disabled)')
    if (!checkboxes.length) return

    const current = [...this.selectedValue]
    const seen = new Set(current.map(i => this.key(i)))
    checkboxes.forEach(cb => {
      const item = {
        id: parseInt(cb.dataset.itemId),
        type: cb.dataset.itemType,
        label: cb.dataset.itemLabel,
        title: cb.dataset.itemTitle,
        subtitle: cb.dataset.itemSubtitle,
        image_url: cb.dataset.itemImage
      }
      const k = this.key(item)
      if (!seen.has(k)) { current.push(item); seen.add(k) }
    })

    this.selectedValue = current
    this.renderSelected()
    this.searchTarget.value = ""
    this.resultsTarget.innerHTML = ""
    this.resultsTarget.classList.add("hidden")
  }

  remove(event) {
    const k = event.currentTarget.dataset.key
    this.selectedValue = this.selectedValue.filter(i => this.key(i) !== k)
    this.renderSelected()
  }

  renderSelected() {
    const items = this.selectedValue

    if (!items.length) {
      this.selectedTarget.innerHTML = `
        <div class="text-center py-8 text-beige-400 font-fira text-sm">
          Nothing added yet. Use the search above to add books, box sets, or memorabilia.
        </div>`
    } else {
      this.selectedTarget.innerHTML = items.map(item => {
        return `
          <div class="flex items-center gap-3 p-2 bg-beige-50 rounded-lg group">
            <div class="w-10 h-14 flex-shrink-0 bg-beige-200 rounded overflow-hidden flex items-center justify-center">
              ${item.image_url ? `<img src="${this.escapeAttr(item.image_url)}" alt="" class="w-full h-full ${this.fitFor(item)}">` : ''}
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-1.5">
                <span class="text-[10px] font-fira uppercase tracking-wider px-1.5 py-0.5 rounded border ${this.badgeFor(item)}">${this.escapeHtml(this.labelFor(item))}</span>
                <p class="text-sm font-medium text-beige-900 truncate">${this.escapeHtml(item.title)}</p>
              </div>
              ${item.subtitle ? `<p class="text-xs text-beige-500 font-fira truncate mt-0.5">${this.escapeHtml(item.subtitle)}</p>` : ''}
            </div>
            <button type="button"
                    class="p-1 text-beige-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100"
                    data-action="item-selector#remove"
                    data-key="${this.escapeAttr(this.key(item))}"
                    title="Remove">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>`
      }).join("")
    }

    this.renderHiddenInputs()
  }

  // Emit hidden inputs grouped by type into the configured field name. Always
  // emit one empty base input per field name so the param is present even when
  // a type has zero selections — that's how the server knows to clear items of
  // that type that were removed.
  renderHiddenInputs() {
    const fieldNames = this.fieldNamesValue || {}
    let html = ""
    for (const [type, fieldName] of Object.entries(fieldNames)) {
      html += `<input type="hidden" name="${this.escapeAttr(fieldName)}" value="">`
      this.selectedValue
        .filter(i => i.type === type)
        .forEach(i => { html += `<input type="hidden" name="${this.escapeAttr(fieldName)}" value="${i.id}">` })
    }
    this.hiddenInputsTarget.innerHTML = html
  }

  hideResults(event) {
    if (this.resultsTarget.contains(event.relatedTarget)) return
    setTimeout(() => this.resultsTarget.classList.add("hidden"), 200)
  }

  escapeHtml(text) {
    if (!text) return ""
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  escapeAttr(text) {
    if (text === null || text === undefined) return ""
    return String(text).replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
